import { useState, useCallback } from 'react'

/**
 * Hook for managing polygon edit state with undo history.
 * Uses a stack: [null, edit1, edit2, ...] where null = pristine.
 * All coordinate values are in {x, y} pixel space matching CRS.Simple storage format.
 */
export const usePolygonEdit = ({ originalPoints }) => {
  // Stack of editedPoints snapshots; [null] = only initial pristine state
  const [stack, setStack] = useState([null])

  const currentEditedPoints = stack[stack.length - 1]
  const isDirty = currentEditedPoints !== null
  const canUndo = stack.length > 1
  const currentPoints = currentEditedPoints ?? originalPoints ?? []

  const updateVertex = useCallback((index, newPoint) => {
    setStack(prev => {
      const top = prev[prev.length - 1]
      const base = top ?? originalPoints ?? []
      return [...prev, base.map((p, i) => i === index ? newPoint : p)]
    })
  }, [originalPoints])

  const translateAll = useCallback((dx, dy) => {
    setStack(prev => {
      const top = prev[prev.length - 1]
      const base = top ?? originalPoints ?? []
      return [...prev, base.map(p => ({ x: p.x + dx, y: p.y + dy }))]
    })
  }, [originalPoints])

  const insertVertex = useCallback((afterIndex, newPoint) => {
    setStack(prev => {
      const top = prev[prev.length - 1]
      const base = top ?? originalPoints ?? []
      return [...prev, [...base.slice(0, afterIndex + 1), newPoint, ...base.slice(afterIndex + 1)]]
    })
  }, [originalPoints])

  const undo = useCallback(() => {
    setStack(prev => prev.length > 1 ? prev.slice(0, -1) : prev)
  }, [])

  const reset = useCallback(() => setStack([null]), [])

  return { currentPoints, isDirty, canUndo, updateVertex, translateAll, insertVertex, undo, reset }
}
